import type { OrganizationResponse } from '../../api/organization-client/organization-client.type';
import i18n, { baseDictionary } from '../../base-dictionary';

/**
 * Updates i18n resource bundles when switching organizations.
 * This function ensures that stale custom text_config from previous organizations
 * doesn't persist by resetting to the base dictionary and then applying the
 * new organization's text_config if it exists.
 *
 * @param {OrganizationResponse | null} organization - The organization to set. Can be null.
 * @return {void}
 */
function updateI18nResources(organization: OrganizationResponse | null): void {
  try {
    // Always reset 'en' (the base language) since that's where base dictionary lives
    const languagesToReset = new Set<string>(['en']);

    // Also reset any languages that the new organization's text_config uses
    if (organization?.text_config) {
      for (const language of Object.keys(organization.text_config)) {
        languagesToReset.add(language);
      }
    }

    // Also check currently loaded languages in i18n to catch any that might have been added
    // This handles edge cases where a previous org added config for a language not in the new org
    const store = i18n.store as { data?: Record<string, unknown> };
    if (store?.data) {
      for (const language of Object.keys(store.data)) {
        languagesToReset.add(language);
      }
    }

    // CRITICAL: Always restore base dictionary, even when org has no text_config
    for (const lng of languagesToReset) {
      try {
        // Remove the bundle (this removes both base and custom, so we need to restore base)
        i18n.removeResourceBundle(lng, 'translation');

        // ALWAYS re-add the base dictionary for this language (if it exists in base)
        // This ensures base is restored even when org has empty text_config: {}
        const baseResources = baseDictionary[lng as keyof typeof baseDictionary];
        if (baseResources?.translation) {
          i18n.addResourceBundle(
            lng,
            'translation',
            baseResources.translation,
            true, // deep merge
            true, // overwrite
          );
        }
      } catch (error) {
        // Log but don't throw - fail gracefully
        console.warn('Failed to reset i18n resources for language', lng, ':', error);
      }
    }

    // Note: Empty text_config: {} will skip this step, leaving only base dictionary (correct behavior)
    if (organization?.text_config && Object.keys(organization.text_config).length > 0) {
      for (const language of Object.keys(organization.text_config)) {
        try {
          const orgConfig = organization.text_config[language];
          if (orgConfig && typeof orgConfig === 'object' && Object.keys(orgConfig).length > 0) {
            i18n.addResourceBundle(
              language,
              'translation',
              orgConfig,
              true, // deep merge for nested structures
              true, // overwrite to replace conflicting keys
            );
          }
        } catch (error) {
          // Log but don't throw - fail gracefully
          console.warn('Failed to add i18n resources for organization language', language, ':', error);
        }
      }
    }

    // CRITICAL FIX: changeLanguage() alone may not trigger if language hasn't changed
    // Since we're updating resources but language stays the same (usually 'en'),
    // we need to force event emission to ensure React components re-render
    // react-i18next listens to 'languageChanged' and 'loaded' events (per bindI18n config)
    const currentLanguage = i18n.language || 'en';

    // Always force event emission by temporarily changing language, then changing back
    // This ensures React components always re-render, even when language stays the same
    // This is critical when switching from org with config to org without config
    const tempLang = currentLanguage === 'en' ? 'en-US' : 'en';
    i18n
      .changeLanguage(tempLang)
      .then(() => {
        // Now change back to trigger the languageChanged event with updated resources
        i18n.changeLanguage(currentLanguage);
      })
      .catch((error) => {
        // If changeLanguage fails, explicitly emit the events as fallback
        // This ensures React components still update even if language change fails
        console.warn('changeLanguage failed, emitting events directly:', error);
        i18n.emit('languageChanged', currentLanguage);
        i18n.emit('loaded', currentLanguage);
      });
  } catch (error) {
    // Log but don't throw - fail gracefully to prevent breaking the app
    console.error('Failed to update i18n resources:', error);
  }
}

export { updateI18nResources };
